import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { ReviewSchema, ReviewCreateInputSchema, ReviewUpdateInputSchema, type Review, type ReviewCreateInput, type ReviewUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `review` (évaluation d'un tiers, org-wide en v0).
 *
 * Calque le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`ReviewCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 */

export type { ReviewCreateInput };

/** Nom de la collection Firestore de la famille review. */
export const REVIEWS_COLLECTION = 'reviews';

/**
 * Repository typé pour la famille `review`.
 * Toute lecture est validée par `ReviewSchema`, toute écriture par
 * `ReviewCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface ReviewRepository {
  create(input: ReviewCreateInput): Promise<Review>;
  update(id: string, patch: ReviewUpdateInput): Promise<Review>;
  list(options?: ListOptions): Promise<Review[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Review | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Review>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Review>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Review>;
}

const reviewConverter = createConverter<Review>((raw: DocumentData) => ReviewSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createReviewRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): ReviewRepository {
  const col = collection(db, REVIEWS_COLLECTION).withConverter(reviewConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Review>(db, col, REVIEWS_COLLECTION, getActorId);

  return {
    async create(input: ReviewCreateInput): Promise<Review> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = ReviewCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.authorContactId !== undefined) optional['authorContactId'] = fields.authorContactId;
      if (fields.comment !== undefined) optional['comment'] = fields.comment;
      if (fields.bookingId !== undefined) optional['bookingId'] = fields.bookingId;
      if (fields.orderId !== undefined) optional['orderId'] = fields.orderId;
      if (fields.eventId !== undefined) optional['eventId'] = fields.eventId;
      if (fields.ticketId !== undefined) optional['ticketId'] = fields.ticketId;
      if (fields.publishedAt !== undefined) optional['publishedAt'] = fields.publishedAt;
      if (fields.replyMessageId !== undefined) optional['replyMessageId'] = fields.replyMessageId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        rating: fields.rating,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Review);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return ReviewSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: ReviewUpdateInput): Promise<Review> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = ReviewUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, REVIEWS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`review ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Review[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Review | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
